// Fields.jsx
import React from 'react';
import MyColor from 'utils/MyColor';
import styled from 'styled-components';
import TextUtil from 'utils/TextUtil';

export const fieldHeight = 40;
const labelWidthOfPc = 150;
const columnGap = 4;

export const FieldErrorMessage = ({ errorMessage }) => (
  <span className="text-red-500 text-sm font-medium">{errorMessage}</span>
);

export const Input = ({
  hint,
  value,
  onChange,
  isValid = true,
  shouldShowError = true,
  maxLength,
  type = 'text',
  enabled = true,
  backgroundColor = MyColor.gray0,
}) => (
  <input
    value={value}
    onChange={onChange}
    placeholder={hint}
    disabled={!enabled}
    style={{
      height: fieldHeight,
      backgroundColor,
      border: `1px solid ${shouldShowError && !isValid ? MyColor.red50 : MyColor.gray30}`,
      borderRadius: 6,
      padding: 10,
      width: '100%',
    }}
    maxLength={maxLength}
    type={type}
  />
);

/**
 * ReadOnlyRadio 컴포넌트
 * @param {Object} props
 * @param {boolean} props.selected
 * @param {string} [props.label] - 선택 값
 * @param {Object} [props.style] - 선택 값
 */
export const ReadOnlyRadio = ({ selected, label, style }) => (
  <ReadOnlyRadioWrapper style={style}>
    <div
      style={{
        borderRadius: 50,
        width: '23px',
        aspectRatio: '1/1',
        border: ` ${selected ? '7px' : '2px'} solid ${selected ? MyColor.gray60 : MyColor.gray30}`,
      }}
    />
    {label}
  </ReadOnlyRadioWrapper>
);

export const ReadOnlyInput = ({ text, style }) => (
  <StyledReadOnlyInput style={style}>{text}</StyledReadOnlyInput>
);

export const DashedInputs = ({ hintList, valueList, onChangeList, isValidList, maxLengthList }) => (
  <div className="flex gap-2">
    {hintList.map((hint, i) => (
      <React.Fragment key={i}>
        {i !== 0 && <span className="mx-2 text-gray-500 font-medium">-</span>}
        <div className="flex-1">
          <Input
            hint={hint}
            value={valueList[i]}
            onChange={onChangeList?.[i]}
            isValid={isValidList[i]}
            maxLength={maxLengthList[i]}
          />
        </div>
      </React.Fragment>
    ))}
  </div>
);

export const SearchBtn = ({ text, onClick }) => {
  const isEnabled = !!onClick;
  return (
    <button
      onClick={onClick}
      className={`h-10 px-4 rounded-md ${isEnabled ? 'bg-orange-200 text-orange-600' : 'bg-gray-200 text-gray-400'}`}
    >
      {text}
    </button>
  );
};

/**
 * @typedef {Object} LabeledReadOnlyInputsProps
 * @property {string} label
 * @property {string} [value]
 * @property {boolean} [isMobile=false]
 * @property {boolean} [isColumn=false]
 * @property {React.CSSProperties} [style]
 */

/**
 * @param {LabeledReadOnlyInputsProps} props
 */
export const LabeledReadOnlyInput = ({ label, value, isMobile, style, isColumn = false }) => {
  return (
    <Wrapper $isMobile={isMobile} style={{ ...style, flexDirection: isColumn && 'column' }}>
      <Label $isMobile={isMobile} style={isColumn ? { height: 'auto' } : null}>
        {label}
      </Label>
      <ReadOnlyWrapper>
        <ReadOnlyInput text={value} />
      </ReadOnlyWrapper>
    </Wrapper>
  );
};

/**
 * @typedef {Object} LabeledReadOnlyRadioProps
 * @property {string} label
 * @property {boolean} [value]
 * @property {boolean} [isMobile=false]
 * @property {boolean} [isColumn=false]
 * @property {React.CSSProperties} [style]
 */

/**
 * @param {LabeledReadOnlyRadioProps} props
 */
export const LabeledReadOnlyRadio = ({ label, value, isMobile, style, isColumn }) => {
  return (
    <Wrapper $isMobile={isMobile} style={{ ...style, flexDirection: isColumn && 'column' }}>
      <Label $isMobile={isMobile} style={isColumn ? { height: 'auto' } : null}>
        {label}
      </Label>
      <ReadOnlyRadio selected={value} />
    </Wrapper>
  );
};

/**
 * @param {LabeledReadOnlyRadioProps} props
 */
export const ReadOnlyRadioWithRightLabel = ({ label, value, isMobile, style }) => {
  return (
    <Wrapper $isMobile={isMobile} style={style}>
      <ReadOnlyRadio selected={value} />
      <Label $isMobile={isMobile}>{label}</Label>
    </Wrapper>
  );
};

export const LabeledReadOnlyInputs = ({ label, values = [], isMobile }) => {
  return (
    <Wrapper $isMobile={isMobile}>
      <Label $isMobile={isMobile}>{label}</Label>
      <ReadOnlyWrapper>
        {values.map((val, idx) => (
          <ReadOnlyInput key={idx} text={val} />
        ))}
      </ReadOnlyWrapper>
    </Wrapper>
  );
};

export const LabeledReadOnlySplitedInput = ({ label, values = [], isMobile, style }) => {
  const labelWidth = isMobile ? 'auto' : `${labelWidthOfPc + columnGap}px`;

  return [
    <Wrapper $isMobile={isMobile} key="-1" style={style}>
      <Label $isMobile={isMobile}>{label}</Label>
      <ReadOnlyWrapper>
        <StyledReadOnlyInputForStart>{values[0]}</StyledReadOnlyInputForStart>
      </ReadOnlyWrapper>
    </Wrapper>,

    values.slice(1).map((v, i) => (
      <div key={i} style={{ marginLeft: labelWidth }}>
        {i === values.length - 2 ? (
          <StyledReadOnlyInputForEnd>{v}</StyledReadOnlyInputForEnd>
        ) : (
          <StyledReadOnlyInputForMiddle>{v}</StyledReadOnlyInputForMiddle>
        )}
      </div>
    )),
  ];
};

// type RadioItem = {
//   label: string;
//   selected: boolean;
// };

// type ReadOnlyRadiosProps = {
//   items: RadioItem[];
// };

/**
 * @typedef {Object} RadioItem
 * @property {string} label
 * @property {boolean} selected
 */

/**
 * @typedef {Object} ReadOnlyRadiosProps
 * @property {RadioItem[]} items
 * @param {Object} [props.style] - 선택 값
 */

/**
 * @param {ReadOnlyRadiosProps} props
 */
export const ReadOnlyRadios = ({ items, style }) => {
  return (
    <StyledReadOnlyRadios style={style}>
      {items.map((item, idx) => (
        <ReadOnlyRadio key={idx} label={item.label} selected={item.selected} />
      ))}
    </StyledReadOnlyRadios>
  );
};

/**
 * @typedef {Object} LabeledReadOnlyRadiosProps
 * @property {RadioItem[]} items
 * @property {string} label
 * @property {bool} isMobile
 * @param {Object} [props.style] - 선택 값
 */

/**
 * @param {LabeledReadOnlyRadiosProps} props
 */
export const LabeledReadOnlyRadios = ({ isMobile, label, items, style }) => {
  return (
    <Wrapper $isMobile={isMobile} style={style}>
      <Label $isMobile={isMobile}>{label}</Label>
      <ReadOnlyRadios items={items} />
    </Wrapper>
  );
};

// styled-components
const StyledReadOnlyRadios = styled.div`
  display: flex;
  gap: 22px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.$isMobile ? 'column' : 'row')};
  align-items: flex-start;
  column-gap: ${columnGap}px;
  row-gap: 12px;
`;

const ReadOnlyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1; // 가능한 최대 너비 차지
  width: 100%; // 모바일에서도 꽉 차게
`;

const StyledReadOnlyInput = styled.div`
  border-radius: 6px;
  padding: 9px 11px;
  border: 1px solid ${MyColor.gray10};
  background-color: ${MyColor.gray5};
  min-height: ${fieldHeight}px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  ${TextUtil.text13w500}
  line-height: 1.6;
`;

const StyledReadOnlyInputForStart = styled(StyledReadOnlyInput)`
  border-bottom-left-radius: 0; /* 아래 왼쪽 */
  border-bottom-right-radius: 0; /* 아래 오른쪽 */
  padding-bottom: 0;
  border-bottom: none;
`;
const StyledReadOnlyInputForMiddle = styled(StyledReadOnlyInput)`
  border-radius: 0;
  padding-top: 0;
  padding-bottom: 0;
  border-top: none;
  border-bottom: none;
`;
const StyledReadOnlyInputForEnd = styled(StyledReadOnlyInput)`
  border-top-left-radius: 0; /* 위 왼쪽 */
  border-top-right-radius: 0; /* 위 오른쪽 */
  padding-top: 0;
  border-top: none;
`;

export const Label = styled.div`
  display: flex;
  align-items: center;
  height: ${(props) =>
    props.$isMobile || props.$needFixedSize === false ? 'auto' : `${fieldHeight}px`};
  width: ${(props) =>
    props.$isMobile || props.$needFixedSize === false ? 'auto' : `${labelWidthOfPc}px`};
  font-size: 16px;
  ${TextUtil.text16Bold}
  font-weight: 500;
  color: ${MyColor.gray50};
`;

const ReadOnlyRadioWrapper = styled.div`
  display: flex;
  gap: 8px;
  height: ${fieldHeight}px;
  align-items: center;
  ${TextUtil.text14Normal}
  color: ${({ $selected }) => ($selected ? MyColor.gray40 : MyColor.gray70)};
`;
